import { z } from 'zod';

export type PreliminaryPrice = z.infer<typeof preliminaryPriceSchema>;
export const preliminaryPriceSchema = z.object({
  time: z.string().datetime({ offset: true }),
  close: z.number(),
});

export type Resolution = `${number}${'m' | 'h' | 'D' | 'W' | 'M' | 'Y'}`;
export type PriceHistory = z.infer<typeof priceHistorySchema>;

export const priceHistorySchema = z.array(
  z.object({
    time: z.string().datetime(),
    open: z.number(),
    high: z.number(),
    low: z.number(),
    close: z.number(),
  })
);

export type PriceChange = z.infer<typeof priceChangeSchema>;
export const priceChangeSchema = z.array(
  z.object({
    time: z.string().datetime(),
    value: z.number(),
  })
);

/**
 * Optional venue params introduced by the price-history `/v2` API (DATA-1542).
 * `/v1` does NOT accept these; only sent on v2 requests.
 */
export type PriceVenue = {
  /** Venue code, e.g. `LSE`, `FSE`, `OMXC`, `CBOE`. */
  exchange?: string;
  /** Two-letter ISO from the entity's `trading_country` (backend defaults to US). */
  country?: string;
};

/**
 * Price-history endpoints. The `*V2` variants use the `/v2` API (DATA-1542),
 * which accepts the `exchange`/`country` venue params; the `/v1` variants ignore
 * them. Prefer the `*V2` paths behind the `DATA-1542-price-history-v2` flag.
 */
export const price = {
  prices: { path: `price-history/v1/prices` },
  delayedPrices: { path: `price-history/v1/delayed` },
  priceChange: { path: `price-history/v1/price-change` },
  preliminaryPrice: {
    path: (subscribableTicker: string) =>
      `price-history/v1/market-close?ticker=${subscribableTicker}`,
  },
  pricesV2: { path: `price-history/v2/prices` },
  delayedPricesV2: { path: `price-history/v2/delayed` },
  priceChangeV2: { path: `price-history/v2/price-change` },
  preliminaryPriceV2: {
    path: (subscribableTicker: string) =>
      `price-history/v2/market-close?ticker=${subscribableTicker}`,
  },
} as const;
